'use client';

import * as React from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { Button } from '../utils/components/ui/button';
import { cn } from '../utils/cn';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../utils/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../utils/components/ui/command';

export interface ComboOption {
  value: string;
  label: string;
}

interface ComboboxProps {
  options: ComboOption[];
  value: string;
  onChange: (value: string) => void;
  defaultValue?: string;
  placeholder?: string;
  className?: string;
  allowAddOption?: boolean;
  useApiFilter?: boolean;
  apiFilterFunction?: (input: string) => Promise<ComboOption[]> | ComboOption[];
  loading?: boolean;
  onSearchChange?: (input: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  selectedLabel?: string;
  loadError?: string;
  onRetry?: () => void;
}

export const Combobox: React.FC<ComboboxProps> = ({
  options,
  value,
  onChange,
  defaultValue = '',
  placeholder = 'Select...',
  className = '',
  allowAddOption = true,
  useApiFilter = false,
  apiFilterFunction,
  loading = false,
  onSearchChange,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  selectedLabel: selectedLabelOverride,
  loadError,
  onRetry,
}) => {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');

  const [currentValue, setCurrentValue] = React.useState('');

  const selectedLabel =
    selectedLabelOverride ||
    options.find(opt => String(opt.value) === String(value))?.label ||
    currentValue;

  const handleSelect = (val: string) => {
    onChange(val);
    setCurrentValue(val);
    setOpen(false);
    setInputValue('');
    onSearchChange?.('');
  };

  const handleAddOption = () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) return;
    const exists = options.some(
      opt => opt.label.toLowerCase() === trimmedValue.toLowerCase()
    );
    if (exists) return;
    const newOption: ComboOption = {
      value: trimmedValue.toLowerCase().replace(/\s+/g, '-'),
      label: trimmedValue,
    };

    if (typeof (window as any).onAddOption === 'function') {
      (window as any).onAddOption(newOption);
    }
    handleSelect(newOption.label);
    setInputValue('');
  };

  React.useEffect(() => {
    if (useApiFilter && apiFilterFunction) {
      apiFilterFunction(inputValue);
    }
  }, [apiFilterFunction, inputValue, useApiFilter]);

  // Restore filtered options logic
  const displayOptions = React.useMemo(() => {
    if (useApiFilter) {
      return options;
    }
    const search = inputValue.toLowerCase();
    return options.filter(option =>
      option.label.toLowerCase().includes(search)
    );
  }, [options, inputValue, useApiFilter]);

  const EmptyState = onSearchChange ? 'div' : CommandEmpty;
  return (
    <Popover
      open={open}
      onOpenChange={nextOpen => {
        setOpen(nextOpen);
        if (onSearchChange) {
          setInputValue('');
          onSearchChange('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-[200px] min-w-0 justify-between', className)}
        >
          <span className="min-w-0 flex-1 truncate text-left">
            {isNaN(Number(selectedLabel)) ? selectedLabel : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command shouldFilter={onSearchChange ? false : undefined}>
          <CommandInput
            placeholder="Search..."
            className="h-9"
            value={inputValue}
            onValueChange={input => {
              setInputValue(input);
              onSearchChange?.(input);
            }}
          />
          <CommandList
            onScroll={event => {
              const list = event.currentTarget;
              if (
                hasMore &&
                !loading &&
                !loadingMore &&
                !loadError &&
                list.scrollHeight - list.scrollTop - list.clientHeight < 64
              ) {
                onLoadMore?.();
              }
            }}
          >
            {allowAddOption &&
              !loading &&
              !loadingMore &&
              !loadError &&
              !hasMore &&
              inputValue.trim() &&
              displayOptions?.length === 0 && (
                <EmptyState>
                  <div
                    className="flex items-center cursor-pointer px-2 py-1 hover:bg-accent"
                    onClick={handleAddOption}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') handleAddOption();
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Add ${inputValue}`}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add “{inputValue}”
                  </div>
                </EmptyState>
              )}
            {loading ? (
              <div className="flex items-center justify-center py-4 text-gray-500">
                <svg
                  className="animate-spin h-5 w-5 mr-2 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
                Loading...
              </div>
            ) : (
              <>
                {/* <div
                  style={{
                    fontSize: '12px',
                    color: '#888',
                    padding: '4px 8px',
                  }}
                >
                  <strong>Debug displayOptions:</strong>
                  <pre>{JSON.stringify(displayOptions, null, 2)}</pre>
                </div> */}
                <ul>
                  {displayOptions?.map(opt => (
                    <li
                      style={{
                        padding: '8px',
                        background:
                          String(value) === String(opt.value)
                            ? '#1A5EF9'
                            : 'transparent',
                        color:
                          String(value) === String(opt.value)
                            ? 'white'
                            : 'black',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleSelect(opt.value)}
                      key={opt.value}
                    >
                      {opt.label}
                    </li>
                  ))}
                </ul>
                {/* <CommandGroup>
                  {displayOptions?.map(opt => (
                    <CommandItem
                      key={opt.value}
                      value={opt.value}
                      onSelect={() => handleSelect(opt.value)}
                      style={{
                        background: '#ffeeba',
                        border: '1px solid #ffc107',
                        margin: '2px 0',
                      }} // debug style
                    >
                      {opt?.label}
                      <Check
                        className={cn(
                          'ml-auto',
                          value === opt?.value ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup> */}
              </>
            )}
            {loadingMore && (
              <div
                role="status"
                className="p-3 text-center text-sm text-gray-500"
              >
                Loading more...
              </div>
            )}
            {loadError && (
              <div role="alert" className="p-3 text-center text-sm">
                <p>{loadError}</p>
                <button
                  type="button"
                  onClick={onRetry}
                  className="mt-2 text-blue-600"
                >
                  Try again
                </button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
